/**
 * Customer-Portal app entry — `<BrowserRouter>` + AuthProvider +
 * protected app-shell + public auth pages.
 *
 * Served at the root of account.orboto.io (OCP-124). No URL prefix —
 * the subdomain IS the namespace.
 *
 * Route table:
 *   /login          → LoginPage (public, two-step credentials + TOTP)
 *   /signup         → SignupPage (public)
 *   /password-reset → PasswordResetPage (public)
 *   /               → OverviewPage (protected)
 *   /products
 *   /subscriptions
 *   /mail
 *   /licenses
 *   /billing
 *   /ai             → AiUsagePage (orboto AI usage dashboard, OCP-496)
 *   /api-keys
 *   /settings       → SettingsPage (profile / security / team, OCP-477)
 *   /*              → NotFoundPage (protected)
 */
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from './components/AppShell';
import { AppVersionBadge } from './components/AppVersionBadge';
import { LegacyPrefixRedirect } from './components/LegacyPrefixRedirect';
import { MailFeatureGuard } from './components/MailFeatureGuard';
import { MailLayout } from './components/MailLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './lib/auth-context';
import { LoginPage } from './routes/LoginPage';
import { NotFoundPage } from './routes/NotFoundPage';
import { OverviewPage } from './routes/OverviewPage';
import { PasswordResetConfirmPage } from './routes/PasswordResetConfirmPage';
import { PasswordResetPage } from './routes/PasswordResetPage';
import { SettingsPage } from './routes/SettingsPage';
import { AffiliatePage } from './routes/AffiliatePage';
import { AiUsagePage } from './routes/AiUsagePage';
import { ReferPage } from './routes/ReferPage';
import { ApiKeysPage } from './routes/ApiKeysPage';
import { BillingPage } from './routes/BillingPage';
import { MailConnectionsPage } from './routes/MailConnectionsPage';
import { MailOverviewPage } from './routes/MailOverviewPage';
import { MailSendsPage } from './routes/MailSendsPage';
import { MailSettingsPage } from './routes/MailSettingsPage';
import { MailApiKeysPage } from './routes/MailApiKeysPage';
import { MailDomainsPage } from './routes/MailDomainsPage';
import { MailSignupRedirect } from './routes/MailSignupRedirect';
import { MailWebhooksPage } from './routes/MailWebhooksPage';
import { MailTemplatesPage } from './routes/MailTemplatesPage';
import { MailSuppressionPage } from './routes/MailSuppressionPage';
import { InstanceDetailPage } from './routes/InstanceDetailPage';
import { InstancesPage } from './routes/InstancesPage';
import { BackupsPage } from './routes/BackupsPage';
import { LicenseDetailPage } from './routes/LicenseDetailPage';
import { LicensesPage } from './routes/LicensesPage';
import { ProductsPage } from './routes/ProductsPage';
import { SignupPage } from './routes/SignupPage';
import { TermsPage } from './routes/TermsPage';
import { ServicePricesPage } from './routes/ServicePricesPage';
import { TermsDecidePage } from './routes/TermsDecidePage';
import { SubscriptionDetailPage } from './routes/SubscriptionDetailPage';
import { SubscriptionsPage } from './routes/SubscriptionsPage';
import { OAuthStartPage } from './routes/OAuthStartPage';
import { RedeemPage } from './routes/RedeemPage';
import { VerifyEmailPage } from './routes/VerifyEmailPage';

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <AppVersionBadge />
        <Routes>
          {/* Pre-OCP-124 the customer-portal SPA lived at /portal/*.
              Catch any stale bookmarks, email-link redirects, or
              post-login `from` state still pointing at the old prefix
              and rewrite to the prefix-less equivalent BEFORE
              ProtectedRoute captures it. */}
          <Route path="/portal" element={<LegacyPrefixRedirect prefix="/portal" />} />
          <Route path="/portal/*" element={<LegacyPrefixRedirect prefix="/portal" />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          {/* OCP-525 - public Terms text from the acceptance registry. */}
          <Route path="/terms" element={<TermsPage />} />
          {/* OCP-657 - the accept / reject links of the Terms change notice. */}
          <Route path="/terms/decide" element={<TermsDecidePage />} />
          {/* OCP-851 - public service price list (AGB V4 6.6). */}
          <Route path="/service-prices" element={<ServicePricesPage />} />
          <Route path="/password-reset" element={<PasswordResetPage />} />
          <Route path="/password-reset/confirm" element={<PasswordResetConfirmPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          {/* OCP-264 — /redeem is auth-aware and handles its own
              unauth chooser. Must NOT sit inside ProtectedRoute so a
              code-link from OWS lands on the chooser, not /login. */}
          <Route path="/redeem" element={<RedeemPage />} />
          {/* /oauth/start is the courtesy landing after an api-key
              reissue redirects the customer back to account.orboto.io.
              Auth-aware (handles its own /login bounce). */}
          <Route path="/oauth/start" element={<OAuthStartPage />} />
          {/* OCP-174 — product-CTA landing. Marketing links to
              account.orboto.io/mail/signup; we route by auth state. */}
          <Route path="/mail/signup" element={<MailSignupRedirect />} />
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >
            <Route index element={<OverviewPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="subscriptions" element={<SubscriptionsPage />} />
            <Route path="subscriptions/:id" element={<SubscriptionDetailPage />} />
            {/* OCP-128 — nested /mail/* under shared MailLayout (tabs
                strip + page header). Each tab page renders inside the
                layout's <Outlet/>. Placeholders ship for unfinished
                tabs so the tabs strip is fully discoverable from day
                one; the per-tab tickets (OCP-129..132, 162, 170, 171)
                fill in real content. */}
            <Route path="mail" element={<MailLayout />}>
              <Route index element={<MailOverviewPage />} />
              {/* OCP-212: Overview + Sends accessible to every customer;
                  the rest of the surface (anything that lets the
                  customer expand their mail-sending capability) is
                  gated on holding an explicit OMS subscription. */}
              <Route path="sends" element={<MailSendsPage />} />
              <Route
                path="connections"
                element={<MailFeatureGuard><MailConnectionsPage /></MailFeatureGuard>}
              />
              <Route
                path="domains"
                element={<MailFeatureGuard><MailDomainsPage /></MailFeatureGuard>}
              />
              <Route
                path="templates"
                element={<MailFeatureGuard><MailTemplatesPage /></MailFeatureGuard>}
              />
              <Route
                path="suppression"
                element={<MailFeatureGuard><MailSuppressionPage /></MailFeatureGuard>}
              />
              <Route
                path="api-keys"
                element={<MailFeatureGuard><MailApiKeysPage /></MailFeatureGuard>}
              />
              <Route
                path="webhooks"
                element={<MailFeatureGuard><MailWebhooksPage /></MailFeatureGuard>}
              />
              <Route
                path="settings"
                element={<MailFeatureGuard><MailSettingsPage /></MailFeatureGuard>}
              />
            </Route>
            <Route path="instances" element={<InstancesPage />} />
            <Route path="instances/:id" element={<InstanceDetailPage />} />
            <Route path="backups" element={<BackupsPage />} />
            <Route path="licenses" element={<LicensesPage />} />
            <Route path="licenses/:id" element={<LicenseDetailPage />} />
            <Route path="billing" element={<BillingPage />} />
            {/* OCP-496: "orboto AI" usage dashboard - available to every account. */}
            <Route path="ai" element={<AiUsagePage />} />
            <Route path="affiliate" element={<AffiliatePage />} />
            {/* OCP-383: refer-a-friend - available to every customer, no gating. */}
            <Route path="refer" element={<ReferPage />} />
            <Route path="api-keys" element={<ApiKeysPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
